import React, { useEffect, useState, useMemo } from 'react';

interface Firework {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  delay: number;
}

const FIREWORK_COLORS = [
  '#FFD700', // Gold
  '#FF6B6B', // Coral
  '#4ECDC4', // Teal
  '#A855F7', // Purple
  '#F472B6', // Pink
  '#60A5FA', // Blue
];

/**
 * NewYearFireworks
 * Subtle background firework animations for the New Year theme
 * Only visible occasionally and in non-distracting positions
 */
export const NewYearFireworks: React.FC = () => {
  const [fireworks, setFireworks] = useState<Firework[]>([]);
  const [cycleKey, setCycleKey] = useState(0);

  // Generate random fireworks periodically
  useEffect(() => {
    const generateFireworks = () => {
      const count = Math.floor(Math.random() * 3) + 2; // 2-4 fireworks
      const newFireworks: Firework[] = [];

      for (let i = 0; i < count; i++) {
        newFireworks.push({
          id: i,
          x: Math.random() * 80 + 10, // 10-90% of width
          y: Math.random() * 40 + 10, // 10-50% of height (top half only)
          color: FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)],
          size: Math.random() * 30 + 20, // 20-50px
          delay: Math.random() * 2, // 0-2s delay
        });
      }

      setFireworks(newFireworks);
      setCycleKey(prev => prev + 1);
    };

    // Initial burst
    generateFireworks();

    // New burst every 8-12 seconds
    const interval = setInterval(() => {
      generateFireworks();
    }, 8000 + Math.random() * 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden opacity-40">
      {fireworks.map((fw) => (
        <div
          key={`${cycleKey}-${fw.id}`}
          className="absolute animate-firework"
          style={{
            left: `${fw.x}%`,
            top: `${fw.y}%`,
            animationDelay: `${fw.delay}s`,
          }}
        >
          {/* Firework burst */}
          <div className="relative">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 rounded-full animate-firework-particle"
                style={{
                  backgroundColor: fw.color,
                  transform: `rotate(${i * 45}deg) translateY(-${fw.size}px)`,
                  animationDelay: `${fw.delay + 0.1 * i}s`,
                  boxShadow: `0 0 6px 2px ${fw.color}`,
                }}
              />
            ))}
            {/* Center glow */}
            <div
              className="absolute w-2 h-2 rounded-full -translate-x-1/2 -translate-y-1/2"
              style={{
                backgroundColor: fw.color,
                boxShadow: `0 0 20px 10px ${fw.color}40`,
              }}
            />
          </div>
        </div>
      ))}

      <style>{`
        @keyframes firework {
          0% {
            opacity: 0;
            transform: scale(0.5);
          }
          20% {
            opacity: 1;
            transform: scale(1);
          }
          100% {
            opacity: 0;
            transform: scale(1.2);
          }
        }

        @keyframes firework-particle {
          0% {
            opacity: 1;
            transform: rotate(var(--rotation)) translateY(0);
          }
          100% {
            opacity: 0;
            transform: rotate(var(--rotation)) translateY(-40px);
          }
        }

        .animate-firework {
          animation: firework 3s ease-out forwards;
        }

        .animate-firework-particle {
          animation: firework-particle 2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default NewYearFireworks;
